import { useLayoutEffect, useState } from 'react';
import styled from '@emotion/styled';
import { filterTokens } from '@/components/common/filter';
import { claudeTokens } from '@/styles/claudeTokens';
import { designSystem } from '@/styles/theme';
import { CONTENT_PADDING_X } from '@/styles/layout';

/**
 * 네 목록의 흰 컨테이너. PC 본문은 앱 헤더와 정렬하고 폰 목록은 좌우 16px이다.
 * 아래 여백: 폰은 앱 본문(App.tsx `#app-content`)의 paddingBottom 16px 이 푸터와의 간격을 만든다. PC 는 앱 본문 아래 여백이 0 이라
 * 흰 컨테이너가 푸터에 붙었다(2026-10-12 자주 묻는 질문 PC 캡처) — 같은 16px 을 PC 에서만 셸 아래 margin 으로 둔다(UI 가이드 6.1).
 */
export const ListPageShell = styled.div`
    display: flow-root;
    box-sizing: border-box;
    width: calc(100% + ${CONTENT_PADDING_X} * 2);
    margin: 20px -${CONTENT_PADDING_X} 16px;
    padding: 0 ${CONTENT_PADDING_X};
    background: ${filterTokens.color.surface};
    border-radius: ${claudeTokens.radius.md};

    @media (max-width: 768px) {
        width: 100%;
        margin: 8px 0 0;
        padding: 0 8px;
    }
`;

/**
 * 탭 줄 — **이 줄만** 앱 헤더 바로 아래에 고정한다(PC·모바일 공통). 필터 줄은 목록과 함께 올라간다(2026-10-01 사용자 지시:
 * 탭 + 필터를 같이 고정하니 머리가 화면을 너무 많이 차지했다).
 * 고정 위치(`top`)는 화면이 `useListHeaderHeight(true)` 값을 style 로 넘긴다. 목록 끝까지 붙어 있으려면 **컨테이너(ListPageShell)의
 * 바로 아래 자식**이어야 한다 — 다른 div 로 감싸면 그 div 가 지나갈 때 함께 올라간다. 상위에 overflow 를 걸면 sticky 가 깨진다.
 */
export const ListTabRow = styled.div`
    position: sticky;
    top: 0;
    z-index: ${designSystem.zIndex.dropdown};
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    min-height: 48px;
    /* 고정됐을 때 좌우 여백 뒤로 목록이 비치지 않게 컨테이너 끝까지 흰 바탕을 칠한다. 아래 1px 은 구분선 자리 */
    margin: 0 -${CONTENT_PADDING_X};
    padding: 0 ${CONTENT_PADDING_X} 1px;
    background: ${filterTokens.color.surface};
    border-radius: ${claudeTokens.radius.md} ${claudeTokens.radius.md} 0 0;

    /* 구분선은 본문 폭만큼만(좌우 여백 안쪽) */
    &::after {
        content: '';
        position: absolute;
        left: ${CONTENT_PADDING_X};
        right: ${CONTENT_PADDING_X};
        bottom: 0;
        height: 1px;
        background: ${filterTokens.color.divider};
    }

    @media (max-width: 768px) {
        margin: 0 -8px;
        padding: 0 8px 1px;

        &::after {
            left: 8px;
            right: 8px;
        }
    }

    /* PC: 생성 버튼(32px) 위아래로 12px 씩 남게 줄을 높인다(2026-10-01 — 버튼이 줄에 꽉 차 보이던 것).
       탭은 줄 높이를 채워 글자가 세로 가운데에 오고, 선택 밑줄은 줄 바닥에 그대로 붙는다. 모바일은 48px 그대로 */
    @media (min-width: 769px) {
        min-height: 56px;
        align-items: stretch;
    }
`;

export const ListTabs = styled.div`
    display: flex;
    min-width: 0;
    @media (max-width: 768px) { flex: 1; }
`;

export const ListTypeTab = styled.button<{ active: boolean }>`
    appearance: none;
    border: none;
    background: none;
    box-shadow: ${({ active }) => active ? `inset 0 -2.5px ${filterTokens.color.accent}` : 'none'};
    padding: 13px 18px;
    font-family: inherit;
    font-size: 15px;
    font-weight: ${({ active }) => active ? 800 : 600};
    color: ${({ active }) => active ? filterTokens.color.text : filterTokens.color.textMuted};
    cursor: pointer;
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent;

    &:focus-visible {
        outline: 2px solid ${filterTokens.color.accent};
        outline-offset: -2px;
    }

    @media (max-width: 768px) {
        flex: 1;
        min-width: 0;
        padding: 13px 0;
        font-size: 16px;
    }
`;

/**
 * PC 생성 버튼(공고 등록·세미나 등록·글쓰기) — 탭 줄 오른쪽 끝. 76×32(최소 너비)·반경 16, 좌우 안쪽 14px, 글자 13px.
 * 탭 줄(PC 56px) 가운데에 놓여 위아래 12px 가 남는다. 모바일은 화면마다 쓰던 방식(떠 있는 버튼 등)을 유지하므로 숨긴다.
 */
export const ListCreateButton = styled.button`
    align-self: center;
    flex-shrink: 0;
    margin-left: auto;
    height: 32px;
    /* 글자가 짧아도(글쓰기) 공고 등록과 같은 크기(76×32) — 더 긴 글자만 넓어진다 */
    min-width: 76px;
    padding: 0 14px;
    border: none;
    border-radius: 16px;
    background: ${designSystem.colors.primary};
    color: #fff;
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${filterTokens.color.accent};
        outline-offset: 2px;
    }
    &:disabled {
        background: ${claudeTokens.color.bgSunken};
        color: ${claudeTokens.color.text2};
        cursor: default;
    }
    @media (max-width: 768px) { display: none; }
`;

/** 필터 줄 — 고정하지 않는다(목록과 함께 올라간다). 아래 구분선은 컨테이너 끝까지 */
export const ListFilterRow = styled.div`
    margin: 0 -${CONTENT_PADDING_X};
    padding: 10px ${CONTENT_PADDING_X};
    border-bottom: 1px solid ${filterTokens.color.divider};

    @media (max-width: 768px) {
        margin: 0 -8px;
        padding: 10px 8px;
    }
`;
export const ListBody = styled.div`
    padding-bottom: 24px;
    /* 하단 배너가 있으면 목록 끝 패딩과 배너 여백을 중복하지 않는다. */
    &:has([data-list-banners="bottom"]) {
        padding-bottom: 16px;
        [data-recruitment-mobile-list] { padding-bottom: 0; }
        @media (max-width: 768px) { padding-bottom: 8px; }
    }
`;

/** 앱 헤더 높이를 따라 안내 띠가 닫히거나 화면이 회전해도 필터가 겹치지 않게 한다. */
export const useListHeaderHeight = (enabled: boolean): number => {
    const [height, setHeight] = useState(0);
    useLayoutEffect(() => {
        if (!enabled) return;
        const header = document.querySelector('header');
        if (!header) return;
        const measure = () => setHeight(header.getBoundingClientRect().height);
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        observer?.observe(header);
        window.addEventListener('resize', measure);
        return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
    }, [enabled]);
    return height;
};
